import React from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator,
  FileText,
  Users,
  Building2,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  BarChart3,
  CreditCard,
  Globe,
  Smartphone,
  Menu,
  X
} from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SupportModal from '../components/SupportModal';

const HomePage: React.FC = () => {
  const { t, isRTL } = useI18n();
  const { user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = React.useState(false);

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-blue-600">Facturelik</h1>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <LanguageSwitcher />
                {user ? (
                  <Link
                    to="/app"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    {t('navigation.dashboard')}
                  </Link>
                ) : (
                  <>
                    <Link to="/login" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                      {t('navigation.signIn')}
                    </Link>
                    <Link
                      to="/signup"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      {t('navigation.getStarted')}
                    </Link>
                  </>
                )}
              </div>
            </div>
            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <LanguageSwitcher />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="ml-4 inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 focus:outline-none"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {user ? (
                <Link
                  to="/app"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navigation.dashboard')}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('navigation.signIn')}
                  </Link>
                  <Link
                    to="/signup"
                    className="block px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:bg-blue-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('navigation.getStarted')}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('home.hero.title')}
              <span className="text-blue-600"> {t('home.hero.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <Link
                  to="/app"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors inline-flex items-center justify-center"
                >
                  {t('navigation.dashboard')}
                  <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-5 w-5`} />
                </Link>
              ) : (
                <>
                  <Link
                    to="/signup"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors inline-flex items-center justify-center"
                  >
                    {t('home.hero.startTrial')}
                    <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-5 w-5`} />
                  </Link>
                  <Link
                    to="/login"
                    className="border border-gray-300 hover:border-gray-400 text-gray-700 px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
                  >
                    {t('home.hero.signIn')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('home.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('home.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.features.smartInvoicing.title')}</h3>
              <p className="text-gray-600">
                {t('home.features.smartInvoicing.description')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.features.clientManagement.title')}</h3>
              <p className="text-gray-600">
                {t('home.features.clientManagement.description')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.features.companyProfiles.title')}</h3>
              <p className="text-gray-600">
                {t('home.features.companyProfiles.description')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.features.financialDashboard.title')}</h3>
              <p className="text-gray-600">
                {t('home.features.financialDashboard.description')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.features.secureReliable.title')}</h3>
              <p className="text-gray-600">
                {t('home.features.secureReliable.description')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.features.fastEfficient.title')}</h3>
              <p className="text-gray-600">
                {t('home.features.fastEfficient.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('home.benefits.title')}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <CheckCircle className={`h-6 w-6 text-green-500 mt-1 ${isRTL ? 'ml-3' : 'mr-3'} flex-shrink-0`} />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('home.benefits.easyToUse.title')}</h3>
                    <p className="text-gray-600">{t('home.benefits.easyToUse.description')}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className={`h-6 w-6 text-green-500 mt-1 ${isRTL ? 'ml-3' : 'mr-3'} flex-shrink-0`} />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('home.benefits.scalable.title')}</h3>
                    <p className="text-gray-600">{t('home.benefits.scalable.description')}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className={`h-6 w-6 text-green-500 mt-1 ${isRTL ? 'ml-3' : 'mr-3'} flex-shrink-0`} />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('home.benefits.support.title')}</h3>
                    <p className="text-gray-600">{t('home.benefits.support.description')}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className={`h-6 w-6 text-green-500 mt-1 ${isRTL ? 'ml-3' : 'mr-3'} flex-shrink-0`} />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('home.benefits.mobileReady.title')}</h3>
                    <p className="text-gray-600">{t('home.benefits.mobileReady.description')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calculator className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.benefits.getStarted.title')}</h3>
                <p className="text-gray-600 mb-6">
                  {t('home.benefits.getStarted.description')}
                </p>
                {user ? (
                  <Link
                    to="/app"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center"
                  >
                    {t('navigation.dashboard')}
                    <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-4 w-4`} />
                  </Link>
                ) : (
                  <Link
                    to="/signup"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center"
                  >
                    {t('home.benefits.getStarted.createAccount')}
                    <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-4 w-4`} />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Link
                to="/app"
                className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold transition-colors inline-flex items-center justify-center"
              >
                {t('navigation.dashboard')}
                <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-5 w-5`} />
              </Link>
            ) : (
              <Link
                to="/signup"
                className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold transition-colors inline-flex items-center justify-center"
              >
                {t('home.cta.startTrial')}
                <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-5 w-5`} />
              </Link>
            )}
            {!user && (
              <Link
                to="/login"
                className="border border-blue-300 hover:border-blue-200 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
              >
                {t('navigation.signIn')}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Facturelik</h3>
              <p className="text-gray-400">
                {t('home.footer.description')}
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('home.footer.product')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/features" className="hover:text-white transition-colors">{t('home.footer.features')}</Link></li>
                <li><Link to="/security" className="hover:text-white transition-colors">{t('home.footer.security')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('home.footer.support')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button
                    onClick={() => setIsSupportModalOpen(true)}
                    className="hover:text-white transition-colors text-left"
                  >
                    {t('home.footer.contactUs')}
                  </button>
                </li>
                <li><Link to="/docs" className="hover:text-white transition-colors">{t('home.footer.documentation')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('home.footer.company')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">{t('home.footer.about')}</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('home.footer.privacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('home.footer.terms')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>{t('home.footer.copyright')}</p>
          </div>
        </div>
      </footer>
      <SupportModal isOpen={isSupportModalOpen} onClose={() => setIsSupportModalOpen(false)} />
    </div>
  );
};

export default HomePage;
